// Icon-Satz der Anwendung.
//
// Die alte App hatte ihre Icons einzeln als Inline-SVG einkompiliert, ohne
// Bibliothek — aus dem Minifikat ließen sie sich nicht sauber herauslösen.
// Deshalb hier ein eigener Satz im selben Stil: 24×24-Raster, Strichzeichnung,
// `currentColor`, damit jedes Icon die Farbe seines Umfelds annimmt und in
// beiden Themen ohne Zutun stimmt. Keine zusätzliche Abhängigkeit.

import type { ReactNode } from 'react';

export type IconName =
  | 'uebersicht' | 'kasse' | 'lager' | 'rechnungen' | 'kunden' | 'lieferanten'
  | 'retouren' | 'buchhaltung' | 'aufgaben' | 'chat' | 'team' | 'firma'
  | 'belege'
  | 'tarif' | 'uebernahme' | 'mandanten'
  | 'suche' | 'menue' | 'sonne' | 'mond' | 'monitor' | 'sprache'
  | 'euro' | 'bank' | 'warnung' | 'uhr' | 'plus' | 'minus' | 'schliessen' | 'glocke'
  | 'assistent' | 'mikrofon' | 'kamera' | 'anhang' | 'senden' | 'haken' | 'herunterladen'
  | 'stern' | 'kalender' | 'pfeilLinks' | 'pfeilRechts' | 'flagge';

// Jeder Eintrag ist der Inhalt eines <svg viewBox="0 0 24 24">.
// React 19 stellt den globalen JSX-Namespace nicht mehr bereit, daher ReactNode.
const PFADE: Record<IconName, ReactNode> = {
  uebersicht: <><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></>,
  kasse: <><rect x="2" y="7" width="20" height="12" rx="2" /><path d="M2 11h20" /><path d="M6 15h4" /><path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" /></>,
  lager: <><path d="M21 8.5V17a2 2 0 0 1-1.1 1.8l-7 3.5a2 2 0 0 1-1.8 0l-7-3.5A2 2 0 0 1 3 17V8.5" /><path d="m3 8.5 8.1-4a2 2 0 0 1 1.8 0l8.1 4-8.1 4a2 2 0 0 1-1.8 0Z" /><path d="M12 12.5V22" /></>,
  rechnungen: <><path d="M15 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6Z" /><path d="M15 2v4h4" /><path d="M9 12h6" /><path d="M9 16h4" /></>,
  kunden: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 5.5a3.5 3.5 0 0 1 0 6.9" /><path d="M17.5 14.5A5.5 5.5 0 0 1 21.5 20" /></>,
  lieferanten: <><rect x="1" y="6" width="13" height="10" rx="1" /><path d="M14 9h4l3 3v4h-7Z" /><circle cx="5" cy="18.5" r="2" /><circle cx="17" cy="18.5" r="2" /></>,
  // Zwei Blätter hintereinander — die Belegkette vom Angebot zur Rechnung.
  belege: <><path d="M8 4h7l4 4v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" /><path d="M15 4v4h4" /><path d="M4 8v10a3 3 0 0 0 3 3h8" /><path d="M10 13h5" /></>,
  retouren: <><path d="M3 8h13a5 5 0 0 1 0 10H8" /><path d="m7 4-4 4 4 4" /></>,
  buchhaltung: <><path d="M3 3v18h18" /><path d="m7 15 3.5-4 3 2.5L20 7" /><circle cx="20" cy="7" r="1.2" /></>,
  aufgaben: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M8 3v3M16 3v3" /><path d="m8.5 13 2.5 2.5 4.5-5" /></>,
  chat: <><path d="M21 12a8 8 0 0 1-8 8H8l-5 2 1.4-4.2A8 8 0 0 1 13 4a8 8 0 0 1 8 8Z" /><path d="M9 11h8M9 15h5" /></>,
  team: <><circle cx="12" cy="7" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /><path d="M4.5 12.5a3 3 0 0 1-.5-5.8" /><path d="M19.5 12.5a3 3 0 0 0 .5-5.8" /></>,
  firma: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M9 8h1M14 8h1M9 12h1M14 12h1" /><path d="M10 21v-4h4v4" /></>,
  tarif: <><path d="m12 2 2.8 6.1 6.7.8-4.9 4.6 1.3 6.6L12 17l-5.9 3.1 1.3-6.6L2.5 8.9l6.7-.8Z" /></>,
  uebernahme: <><path d="M12 3v12" /><path d="m7 11 5 5 5-5" /><path d="M4 20h16" /></>,
  mandanten: <><path d="m3 10 9-7 9 7v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><path d="M9 21v-7h6v7" /></>,
  suche: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></>,
  menue: <><path d="M4 7h16M4 12h16M4 17h16" /></>,
  sonne: <><circle cx="12" cy="12" r="4.5" /><path d="M12 1.5v2.5M12 20v2.5M1.5 12h2.5M20 12h2.5M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M19.4 4.6l-1.8 1.8M6.4 17.6l-1.8 1.8" /></>,
  mond: <><path d="M20 14.5A8.5 8.5 0 1 1 10 3.2a7 7 0 0 0 10 11.3Z" /></>,
  herunterladen: <><path d="M12 3.5v11" /><path d="m7.5 10.5 4.5 4.5 4.5-4.5" /><path d="M4 16.5v2A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5v-2" /></>,
  monitor: <><rect x="2.5" y="4" width="19" height="12.5" rx="2" /><path d="M8.5 20.5h7M12 16.5v4" /></>,
  sprache: <><circle cx="12" cy="12" r="9" /><path d="M3.2 9h17.6M3.2 15h17.6" /><path d="M12 3a14 14 0 0 0 0 18a14 14 0 0 0 0-18Z" /></>,
  euro: <><path d="M17 6.5A7 7 0 1 0 17 17.5" /><path d="M4 10h8M4 14h8" /></>,
  bank: <><path d="M3 9.5 12 4l9 5.5" /><path d="M4 21h16" /><path d="M5.5 10.5V18M10 10.5V18M14 10.5V18M18.5 10.5V18" /><path d="M4.5 18h15" /></>,
  warnung: <><path d="M10.3 3.9 2.4 17.5A1.9 1.9 0 0 0 4 20.4h16a1.9 1.9 0 0 0 1.6-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z" /><path d="M12 9v4.5M12 17h.01" /></>,
  uhr: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3.5 2" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
  minus: <><path d="M5 12h14" /></>,
  schliessen: <><path d="m6 6 12 12M18 6 6 18" /></>,
  stern: <><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" /><path d="M19 3v4M17 5h4M5 17v4M3 19h4" /></>,
  kalender: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  pfeilLinks: <><path d="m15 18-6-6 6-6" /></>,
  pfeilRechts: <><path d="m9 18 6-6-6-6" /></>,
  flagge: <><path d="M5 21V4" /><path d="M5 4h11l-2 4 2 4H5" /></>,
  // Funkelnd: das gebräuchliche Zeichen für eine KI-Funktion.
  assistent: <><path d="M12 3.5 13.6 8l4.4 1.6L13.6 11 12 15.5 10.4 11 6 9.6 10.4 8Z" /><path d="M18.5 15.5 19.2 17.4 21 18l-1.8.7-.7 1.8-.7-1.8L16 18l1.8-.6Z" /><path d="M5.5 14.5 6 15.9 7.5 16.4 6 17l-.5 1.4L5 17l-1.5-.6L5 15.9Z" /></>,
  mikrofon: <><rect x="9" y="2.5" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0" /><path d="M12 17.5V21" /><path d="M9 21h6" /></>,
  kamera: <><path d="M3 8.5A2 2 0 0 1 5 6.5h1.6l1-2h4.8l1 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><circle cx="12" cy="13" r="3.5" /></>,
  anhang: <><path d="M20 11.5 12.4 19a4.5 4.5 0 0 1-6.4-6.4l7.6-7.6a3 3 0 0 1 4.3 4.3l-7.6 7.6a1.5 1.5 0 0 1-2.2-2.2l6.9-6.9" /></>,
  senden: <><path d="M4 12 20.5 4 13 20.5l-1.8-6.7Z" /><path d="m11.2 13.8 9.3-9.8" /></>,
  haken: <><path d="m4.5 12.5 5 5 10-11" /></>,
  glocke: <><path d="M18 8.5a6 6 0 1 0-12 0c0 5-2.2 6.5-2.2 6.5h16.4S18 13.5 18 8.5" /><path d="M13.7 19a2 2 0 0 1-3.4 0" /></>,
};

export default function Icon({ name, groesse = 20, klasse = '' }: {
  name: IconName;
  groesse?: number;
  klasse?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${klasse}`}
      aria-hidden="true"
      focusable="false"
    >
      {PFADE[name]}
    </svg>
  );
}
